import { useState, useEffect } from "react";
import { Star, Quote, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials?active_only=true`);
      setTestimonials(response.data);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    } finally {
      setLoading(false);
    }
  };

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  if (loading) {
    return (
      <section className="py-20 bg-[#12121a]">
        <div className="flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin" />
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return null;
  }

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="py-20 bg-[#12121a]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-[#00d4aa] font-mono text-sm mb-4 block">
            {'// MÜŞTERİ YORUMLARI'}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#f0f0f5] mb-4">
            Müşterilerimiz Ne Diyor?
          </h2>
          <p className="text-[#9ca3af] max-w-2xl mx-auto">
            Birlikte çalıştığımız müşterilerimizin deneyimleri
          </p>
        </div>

        <div className="relative max-w-4xl mx-auto">
          <div className="bg-[#16161f] border border-[#2a2a3a] rounded-2xl p-8 md:p-12 relative">
            {/* Quote icon */}
            <div className="absolute top-6 left-6 w-12 h-12 rounded-full bg-[#00d4aa]/10 flex items-center justify-center">
              <Quote className="w-6 h-6 text-[#00d4aa]" />
            </div>

            <div className="pt-8">
              {/* Stars */}
              <div className="flex gap-1 mb-6 justify-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${
                      i < currentTestimonial.rating
                        ? 'text-[#fbbf24] fill-[#fbbf24]'
                        : 'text-[#2a2a3a]'
                    }`}
                  />
                ))}
              </div>

              {/* Content */}
              <p className="text-[#f0f0f5] text-lg md:text-xl text-center leading-relaxed mb-8">
                "{currentTestimonial.content}"
              </p>

              {/* Author */}
              <div className="flex items-center justify-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#00d4aa] to-[#0ea5e9] flex items-center justify-center text-[#0a0a0f] font-bold text-lg">
                  {currentTestimonial.name.charAt(0)}
                </div>
                <div className="text-left">
                  <h4 className="text-[#f0f0f5] font-semibold">
                    {currentTestimonial.name}
                  </h4>
                  <p className="text-[#9ca3af] text-sm">
                    {currentTestimonial.role}, {currentTestimonial.company}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          {testimonials.length > 1 && (
            <>
              <button
                onClick={prevTestimonial}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 w-12 h-12 rounded-full bg-[#16161f] border border-[#2a2a3a] flex items-center justify-center text-[#9ca3af] hover:text-[#00d4aa] hover:border-[#00d4aa] transition-all"
                data-testid="testimonial-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextTestimonial}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 w-12 h-12 rounded-full bg-[#16161f] border border-[#2a2a3a] flex items-center justify-center text-[#9ca3af] hover:text-[#00d4aa] hover:border-[#00d4aa] transition-all"
                data-testid="testimonial-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Dots */}
          {testimonials.length > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentIndex
                      ? 'w-8 bg-[#00d4aa]'
                      : 'bg-[#2a2a3a] hover:bg-[#9ca3af]'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
